import React, { useState } from "react";
import { useWallet, useConnection } from "@solana/wallet-adapter-react";
import { PublicKey, Transaction } from "@solana/web3.js";
import {
  createMintToInstruction,
  createBurnInstruction,
  createFreezeAccountInstruction,
  createThawAccountInstruction,
  TOKEN_PROGRAM_ID,
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getMint,
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { Coins, Flame, Settings, Snowflake, Zap } from "lucide-react";

const TOKEN_EXTENSIONS_PROGRAM_ID = TOKEN_PROGRAM_ID;

const TokenActions = ({ mintAddress, onActionComplete, isAdmin }) => {
  const { publicKey, sendTransaction } = useWallet();
  const { connection } = useConnection();
  const { toast } = useToast();

  const [mintAmount, setMintAmount] = useState("");
  const [mintToAddress, setMintToAddress] = useState("");
  const [burnAmount, setBurnAmount] = useState("");
  const [freezeAddress, setFreezeAddress] = useState("");
  const [thawAddress, setThawAddress] = useState("");
  const [loading, setLoading] = useState(null);

  const executeTransaction = async (action, transactionBuilder) => {
    if (!publicKey) {
      toast({
        title: "Wallet not connected",
        description: "Please connect your wallet first",
        variant: "destructive",
      });
      return;
    }

    setLoading(action);
    try {
      console.log(`Starting ${action} transaction...`);
      const transaction = await transactionBuilder();

      transaction.feePayer = publicKey;
      const { blockhash } = await connection.getLatestBlockhash("confirmed");
      transaction.recentBlockhash = blockhash;

      console.log(`${action} transaction prepared:`, transaction);
      console.log(`Transaction fee payer:`, transaction.feePayer?.toString());
      console.log(`Transaction blockhash:`, transaction.recentBlockhash);

      try {
        console.log("Simulating transaction...");
        const simulationResult = await connection.simulateTransaction(
          transaction
        );
        console.log("Simulation result:", simulationResult);

        if (simulationResult.value.err) {
          throw new Error(
            `Transaction simulation failed: ${JSON.stringify(
              simulationResult.value.err
            )}`
          );
        }
        console.log("✅ Simulation successful, proceeding with transaction...");
      } catch (simError) {
        console.error("Simulation error:", simError);
      }

      await new Promise((resolve) => setTimeout(resolve, 100));

      console.log("Sending transaction to wallet...");

      let retryCount = 0;
      const maxRetries = 2;

      while (retryCount <= maxRetries) {
        try {
          const signature = await sendTransaction(transaction, connection, {
            maxRetries: 3,
            preflightCommitment: "confirmed",
            skipPreflight: true,
          });

          console.log(`${action} transaction sent:`, signature);

          const confirmation = await connection.confirmTransaction(
            {
              signature,
              blockhash,
              lastValidBlockHeight: (
                await connection.getLatestBlockhash()
              ).lastValidBlockHeight,
            },
            "confirmed"
          );

          if (confirmation.value.err) {
            throw new Error(`Transaction failed: ${confirmation.value.err}`);
          }

          toast({
            title: `${action} successful!`,
            description: `Transaction: ${signature.slice(
              0,
              8
            )}...${signature.slice(-8)}`,
            variant: "default",
          });
          onActionComplete?.();
          return;
        } catch (walletError) {
          console.error(`Wallet error attempt ${retryCount + 1}:`, walletError);

          if (
            retryCount < maxRetries &&
            walletError instanceof Error &&
            walletError.message.includes("Unexpected error")
          ) {
            retryCount++;
            console.log(
              `Retrying transaction (attempt ${retryCount + 1}/${
                maxRetries + 1
              })...`
            );
            await new Promise((resolve) => setTimeout(resolve, 1000));
            continue;
          } else {
            throw walletError;
          }
        }
      }
    } catch (error) {
      console.error(`Error ${action}:`, error);

      let errorMessage = "Unknown error occurred";
      if (error instanceof Error) {
        errorMessage = error.message;
        console.error(`Full error details:`, {
          name: error.name,
          message: error.message,
          stack: error.stack,
        });
      }

      if (
        errorMessage.includes("User rejected") ||
        errorMessage.includes("user rejected")
      ) {
        errorMessage = "Transaction was rejected by user";
      } else if (errorMessage.includes("insufficient funds")) {
        errorMessage = "Insufficient SOL for transaction fees";
      } else if (errorMessage.includes("Account not found")) {
        errorMessage = "Token account not found. Try creating it first.";
      } else if (errorMessage.includes("Instruction::PausableExtension")) {
        errorMessage = "Token does not support pause functionality";
      } else if (errorMessage.includes("Unexpected error")) {
        errorMessage =
          "Wallet connection error. Please try reconnecting your wallet.";
      }

      toast({
        title: `${action} failed`,
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(null);
    }
  };

  const handleMint = async () => {
    const amount = parseFloat(mintAmount);
    if (!amount || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to mint",
        variant: "destructive",
      });
      return;
    }

    await executeTransaction("Mint", async () => {
      const mintPubkey = new PublicKey(mintAddress);
      const transaction = new Transaction();

      const mintInfo = await getMint(
        connection,
        mintPubkey,
        "confirmed",
        TOKEN_EXTENSIONS_PROGRAM_ID
      );

      const targetAddress = mintToAddress
        ? new PublicKey(mintToAddress)
        : publicKey;

      const associatedTokenAddress = await getAssociatedTokenAddress(
        mintPubkey,
        targetAddress,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      try {
        await connection.getAccountInfo(associatedTokenAddress);
      } catch (error) {
        transaction.add(
          createAssociatedTokenAccountInstruction(
            publicKey,
            associatedTokenAddress,
            targetAddress,
            mintPubkey,
            TOKEN_EXTENSIONS_PROGRAM_ID,
            ASSOCIATED_TOKEN_PROGRAM_ID
          )
        );
      }

      const mintAmountInUnits = Math.floor(
        amount * Math.pow(10, mintInfo.decimals)
      );

      transaction.add(
        createMintToInstruction(
          mintPubkey,
          associatedTokenAddress,
          publicKey,
          mintAmountInUnits,
          [],
          TOKEN_EXTENSIONS_PROGRAM_ID
        )
      );

      return transaction;
    });

    setMintAmount("");
    setMintToAddress("");
  };

  const handleBurn = async () => {
    const amount = parseFloat(burnAmount);
    if (!amount || amount <= 0) {
      toast({
        title: "Invalid amount",
        description: "Please enter a valid amount to burn",
        variant: "destructive",
      });
      return;
    }

    await executeTransaction("Burn", async () => {
      const mintPubkey = new PublicKey(mintAddress);
      const transaction = new Transaction();

      const mintInfo = await getMint(
        connection,
        mintPubkey,
        "confirmed",
        TOKEN_EXTENSIONS_PROGRAM_ID
      );

      const associatedTokenAddress = await getAssociatedTokenAddress(
        mintPubkey,
        publicKey,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      const burnAmountInUnits = Math.floor(
        amount * Math.pow(10, mintInfo.decimals)
      );

      transaction.add(
        createBurnInstruction(
          associatedTokenAddress,
          mintPubkey,
          publicKey,
          burnAmountInUnits,
          [],
          TOKEN_EXTENSIONS_PROGRAM_ID
        )
      );

      return transaction;
    });

    setBurnAmount("");
  };

  const handleFreezeAccount = async () => {
    if (!freezeAddress) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid wallet address to freeze",
        variant: "destructive",
      });
      return;
    }

    try {
      new PublicKey(freezeAddress);
    } catch (error) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid Solana wallet address",
        variant: "destructive",
      });
      return;
    }

    await executeTransaction("Freeze Account", async () => {
      const mintPubkey = new PublicKey(mintAddress);
      const targetAddress = new PublicKey(freezeAddress);
      const transaction = new Transaction();

      const associatedTokenAddress = await getAssociatedTokenAddress(
        mintPubkey,
        targetAddress,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      try {
        await connection.getAccountInfo(associatedTokenAddress);
      } catch (error) {
        throw new Error(
          "Target wallet does not have a token account for this mint"
        );
      }

      transaction.add(
        createFreezeAccountInstruction(
          associatedTokenAddress,
          mintPubkey,
          publicKey,
          [],
          TOKEN_EXTENSIONS_PROGRAM_ID
        )
      );

      return transaction;
    });

    setFreezeAddress("");
  };

  const handleThawAccount = async () => {
    if (!thawAddress) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid wallet address to thaw",
        variant: "destructive",
      });
      return;
    }

    try {
      new PublicKey(thawAddress);
    } catch (error) {
      toast({
        title: "Invalid address",
        description: "Please enter a valid Solana wallet address",
        variant: "destructive",
      });
      return;
    }

    await executeTransaction("Thaw Account", async () => {
      const mintPubkey = new PublicKey(mintAddress);
      const targetAddress = new PublicKey(thawAddress);
      const transaction = new Transaction();

      const associatedTokenAddress = await getAssociatedTokenAddress(
        mintPubkey,
        targetAddress,
        false,
        TOKEN_EXTENSIONS_PROGRAM_ID,
        ASSOCIATED_TOKEN_PROGRAM_ID
      );

      try {
        await connection.getAccountInfo(associatedTokenAddress);
      } catch (error) {
        throw new Error(
          "Target wallet does not have a token account for this mint"
        );
      }

      transaction.add(
        createThawAccountInstruction(
          associatedTokenAddress,
          mintPubkey,
          publicKey,
          [],
          TOKEN_EXTENSIONS_PROGRAM_ID
        )
      );

      return transaction;
    });

    setThawAddress("");
  };

  if (!isAdmin) {
    return (
      <Card className="bg-gradient-card border-border shadow-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Token Actions
          </CardTitle>
          <CardDescription>Admin privileges required</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground text-center py-8">
            You need admin privileges to perform token actions
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="accounts-info-section">
      <div className="section_03">
        <div className="row">
          <div className="col-md-6 mb-4 ">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="bg-gradient-card card-main-wrapper card_01">
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2 text-success">
                    <Coins className="h-5 w-5" />
                    Mint Tokens
                  </CardTitle>
                  <CardDescription>
                    Create new tokens and send them to any account
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 form-group">
                    <Label htmlFor="mint-amount">Amount to Mint</Label>
                    <Input
                      id="mint-amount"
                      type="number"
                      placeholder="1000"
                      value={mintAmount}
                      onChange={(e) => setMintAmount(e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <div className="space-y-2 form-group">
                    <Label htmlFor="mint-to-address">
                      Mint to Address (optional)
                    </Label>
                    <Input
                      id="mint-to-address"
                      type="text"
                      placeholder="Leave empty to mint to your wallet"
                      value={mintToAddress}
                      onChange={(e) => setMintToAddress(e.target.value)}
                      className="bg-background font-mono text-sm"
                    />
                  </div>
                  <Button
                    className="w-full btn-style mt-4"
                    onClick={handleMint}
                    disabled={loading === "Mint" || !mintAmount}
                  >
                    {loading === "Mint" ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    ) : (
                      <>
                        <Coins className="h-4 w-4 mr-2" />
                        Mint Tokens
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="col-md-6 mb-4 ">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="bg-gradient-card card-main-wrapper card_01">
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2 text-destructive">
                    <Flame className="h-5 w-5" />
                    Burn Tokens
                  </CardTitle>
                  <CardDescription>
                    Permanently remove tokens from circulation
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 form-group">
                    <Label htmlFor="burn-amount">Amount to Burn</Label>
                    <Input
                      id="burn-amount"
                      type="number"
                      placeholder="100"
                      value={burnAmount}
                      onChange={(e) => setBurnAmount(e.target.value)}
                      className="bg-background"
                    />
                  </div>
                  <Button
                    variant="destructive"
                    className="btn-style"
                    onClick={handleBurn}
                    disabled={loading === "Burn" || !burnAmount}
                  >
                    {loading === "Burn" ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    ) : (
                      <>
                        <Flame className="h-4 w-4 mr-2" />
                        Burn Tokens
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="col-md-6 mb-4 ">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <Card className="bg-gradient-card card-main-wrapper card_01">
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2 text-warning">
                    <Snowflake className="h-5 w-5" />
                    Freeze Account
                  </CardTitle>
                  <CardDescription>
                    Lock tokens in a specific wallet address
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 form-group">
                    <Label htmlFor="freeze-address">
                      Wallet Address to Freeze
                    </Label>
                    <Input
                      id="freeze-address"
                      type="text"
                      placeholder="Enter wallet address"
                      value={freezeAddress}
                      onChange={(e) => setFreezeAddress(e.target.value)}
                      className="bg-background font-mono text-sm"
                    />
                  </div>
                  <Button
                    className="btn-style"
                    onClick={handleFreezeAccount}
                    disabled={loading === "Freeze Account" || !freezeAddress}
                  >
                    {loading === "Freeze Account" ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    ) : (
                      <>
                        <Snowflake className="h-4 w-4 mr-2" />
                        Freeze Account
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          <div className="col-md-6 mb-4 ">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Card className="bg-gradient-card card-main-wrapper card_01">
                <CardHeader>
                  <CardTitle className="d-flex align-items-center gap-2 text-success">
                    <Zap className="h-5 w-5" />
                    Thaw Account
                  </CardTitle>
                  <CardDescription>
                    Unlock frozen tokens in a specific wallet address
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2 form-group">
                    <Label htmlFor="thaw-address">Wallet Address to Thaw</Label>
                    <Input
                      id="thaw-address"
                      type="text"
                      placeholder="Enter wallet address"
                      value={thawAddress}
                      onChange={(e) => setThawAddress(e.target.value)}
                      className="bg-background font-mono text-sm"
                    />
                  </div>
                  <Button
                    className="btn-style"
                    onClick={handleThawAccount}
                    disabled={loading === "Thaw Account" || !thawAddress}
                  >
                    {loading === "Thaw Account" ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                    ) : (
                      <>
                        <Zap className="h-4 w-4 mr-2" />
                        Thaw Account
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TokenActions;
